import { markRaw } from "vue";
import { defineStore } from "pinia";
import putio from "@/services/putio";
import thumbnailCache from "@/services/thumbnailCache.js";

// In-flight promise for fetchTransfers so N simultaneously-mounting
// TorrentList instances share ONE getTransfers+getInfo round-trip instead of
// firing N. Module-scope (not store state) so it's never persisted.
let transfersInflight = null;

export const usePutioStore = defineStore("putio", {
  state: () => ({
    info: {},
    files: [],
    transfers: [],
    isLoading: false,
    client: null,
    thumbnailsLoading: new Set(), // Track which files are loading thumbnails
  }),
  // Persist ONLY the serializable data worth restoring. `persist: true`
  // JSON.stringified the ENTIRE state synchronously on every mutation —
  // including `client` (the live SDK/axios instance) and `thumbnailsLoading`
  // (a Set that round-trips as `{}` and breaks `.add()` after reload).
  persist: { pick: ["info", "files", "transfers"] },
  actions: {
    async initializeClient() {
      try {
        // putio.client is null until the SDK chunk is lazy-loaded; ensureClient()
        // dynamically imports it (once) and returns the constructed client.
        // markRaw: the client is a live SDK instance — deep-proxying it is
        // pure overhead (and can break internals). The `client` ref itself
        // stays reactive, so `putioStore.client` presence checks still update.
        this.client = markRaw(await putio.ensureClient());
        await this.getInfo();
        return true;
      } catch (error) {
        // 401/403 = unauthenticated app load. apiClient.js already handles
        // the /locked redirect via its own 401 handler on /api/auth/status;
        // logging this as console.error created a Lighthouse BP regression
        // on every cold load. Real (non-auth) failures still surface via
        // console.warn so they remain debuggable.
        const status = error?.response?.status;
        if (status !== 401 && status !== 403) {
          console.warn("Put.io client initialization failed:", error);
        }
        throw error;
      }
    },

    async getInfo() {
      try {
        const response = await this.client.Account.Info();
        this.info = response.data.info;
        return true;
      } catch (error) {
        // See note in initializeClient(): 401/403 means the app is loading
        // unauthenticated and the auth guard is about to redirect to /locked.
        const status = error?.response?.status;
        if (status !== 401 && status !== 403) {
          console.warn("Put.io info failed:", error);
        }
        throw error;
      }
    },

    async getMediaItem(id) {
      try {
        // Get basic file info to determine type
        const basicFile = await putio.getFile(id);

        if (!basicFile) {
          throw new Error("File not found or API returned null");
        }

        let file = basicFile;
        let hlsStreamUrl = null;
        let thumbnail = null;

        if (file.file_type === "VIDEO") {
          try {
            hlsStreamUrl = await putio.getStreamUrl(id);
            thumbnail = await this.getThumbnailAsync(file.name, id);
          } catch (streamError) {
            console.error(
              "[PUTIO] Failed to get video stream URLs:",
              streamError,
            );
          }
        }

        return {
          ...file,
          thumbnail,
          hls_stream_url: hlsStreamUrl,
          mp4_stream_url: file.mp4_stream_url, // Include MP4 stream URL from the API response
        };
      } catch (error) {
        console.error("[PUTIO] Failed to fetch media item:", error);

        // Create a structured error object with more details
        const errorInfo = {
          type: "unknown",
          message: error.message || "Unknown error occurred",
          originalError: error,
        };

        // Determine error type based on the error
        if (error.response) {
          errorInfo.statusCode = error.response.status;
          errorInfo.statusText = error.response.statusText;

          switch (error.response.status) {
            case 404:
              errorInfo.type = "not_found";
              errorInfo.message =
                "File not found. It may have been deleted or moved.";
              break;
            case 401:
            case 403: {
              // Feature 74: a featured-only invite gets 403
              // `restricted_to_featured` for anything outside its curated
              // slice. That is a deliberate boundary, not a broken login —
              // telling the user to "check your authentication" (with a
              // Retry button) would send them chasing a problem that isn't
              // theirs to fix.
              const code =
                error.response?.data?.error ?? error.response?.body?.error;
              if (code === "restricted_to_featured") {
                errorInfo.type = "restricted";
                errorInfo.message =
                  "This item isn't part of the selection shared with you.";
              } else if (code === "restricted_scope_unavailable") {
                errorInfo.type = "server_error";
                errorInfo.message =
                  "Couldn't check what you have access to. Please try again in a moment.";
              } else {
                errorInfo.type = "unauthorized";
                errorInfo.message =
                  "Access denied. Please check your authentication.";
              }
              break;
            }
            case 429:
              errorInfo.type = "rate_limited";
              errorInfo.message = "Too many requests. Please try again later.";
              break;
            case 500:
            case 502:
            case 504:
              errorInfo.type = "server_error";
              errorInfo.message = "Server error. Please try again later.";
              break;
            case 503:
              errorInfo.type = "service_unavailable";
              errorInfo.message =
                "Service temporarily unavailable. Please try again in a moment.";
              break;
            default:
              errorInfo.type = "api_error";
              errorInfo.message = `API error: ${error.response.status} ${error.response.statusText}`;
          }
        } else if (error.code === "NETWORK_ERROR" || !navigator.onLine) {
          errorInfo.type = "network_error";
          errorInfo.message =
            "Network error. Please check your internet connection.";
        } else if (
          error.message.includes("not found") ||
          error.message.includes("null")
        ) {
          errorInfo.type = "not_found";
          errorInfo.message =
            "File not found. It may have been deleted or moved.";
        }

        throw errorInfo;
      }
    },

    async fetchFileById(id) {
      try {
        return await putio.getFile(id);
      } catch (error) {
        console.warn(
          "[PUTIO] fetchFileById failed for",
          id,
          error?.response?.status ?? error?.message ?? error,
        );
        return null;
      }
    },

    async getFolderContents(id) {
      try {
        const files = await putio.getFiles(id);

        // Return files immediately - thumbnails will be loaded lazily by components
        const processedFiles = files.map((file) => ({
          ...file,
          thumbnail: null, // Will be loaded lazily
          thumbnailLoading: false,
        }));

        return processedFiles;
      } catch (error) {
        console.error("Get folder contents error:", error);

        // Create a structured error object similar to getMediaItem
        const errorInfo = {
          type: "unknown",
          message: error.message || "Failed to load folder contents",
          originalError: error,
        };

        if (error.response) {
          errorInfo.statusCode = error.response.status;
          errorInfo.statusText = error.response.statusText;

          switch (error.response.status) {
            case 404:
              errorInfo.type = "not_found";
              errorInfo.message = "Folder not found or has been deleted.";
              break;
            case 401:
            case 403:
              errorInfo.type = "unauthorized";
              errorInfo.message = "Access denied to folder contents.";
              break;
            case 503:
              errorInfo.type = "service_unavailable";
              errorInfo.message =
                "Service temporarily unavailable. Please try again in a moment.";
              break;
            case 500:
            case 502:
            case 504:
              errorInfo.type = "server_error";
              errorInfo.message = "Server error while loading folder contents.";
              break;
            default:
              errorInfo.type = "api_error";
              errorInfo.message = `Failed to load folder contents: ${error.response.status}`;
          }
        } else if (error.code === "NETWORK_ERROR" || !navigator.onLine) {
          errorInfo.type = "network_error";
          errorInfo.message = "Network error while loading folder contents.";
        }

        throw errorInfo;
      }
    },

    async fetchFiles() {
      this.isLoading = true;

      try {
        const files = await putio.getFiles(0); // Root folder

        // Return files immediately - thumbnails will be loaded lazily by components
        const processedFiles = files
          .filter((file) => ["VIDEO", "FOLDER"].includes(file.file_type))
          .map((file) => ({
            ...file,
            thumbnail: null, // Will be loaded lazily
            thumbnailLoading: false,
          }));

        this.files = processedFiles;
      } catch (error) {
        console.error("API Error:", error);
      } finally {
        this.isLoading = false;
      }
    },

    // Get thumbnail with immediate return (for single file requests)
    async getThumbnailAsync(title, fileId = null, priority = "high") {
      try {
        // Add priority-based delay for non-high priority requests
        if (priority === "low") {
          await new Promise((resolve) => setTimeout(resolve, 500));
        } else if (priority === "normal") {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }

        const thumbnail = await thumbnailCache.getThumbnail(title);

        // If we have a fileId, update the file in our store
        if (fileId && thumbnail) {
          this.updateFileThumbnail(fileId, thumbnail);
        }

        return thumbnail;
      } catch (error) {
        console.error("Error getting thumbnail for:", title, error);
        return null;
      }
    },

    // Load thumbnails for multiple files asynchronously
    async loadThumbnailsAsync(files) {
      // Filter for video files only
      const videoFiles = files;

      // Process files in batches to avoid overwhelming the API
      const batchSize = 5;
      const batches = [];

      for (let i = 0; i < videoFiles.length; i += batchSize) {
        batches.push(videoFiles.slice(i, i + batchSize));
      }

      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        const batch = batches[batchIndex];

        // Process batch in parallel
        const promises = batch.map(async (file) => {
          try {
            this.thumbnailsLoading.add(file.id);
            this.updateFileLoadingState(file.id, true);

            const thumbnail = await thumbnailCache.getThumbnail(file.name);

            if (thumbnail) {
              this.updateFileThumbnail(file.id, thumbnail);
            }
          } catch (error) {
            console.error("Error loading thumbnail for:", file.name, error);
          } finally {
            this.thumbnailsLoading.delete(file.id);
            this.updateFileLoadingState(file.id, false);
          }
        });

        await Promise.allSettled(promises);

        // Small delay between batches to be respectful to the API
        if (batchIndex < batches.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
      }
    },

    // Update thumbnail for a specific file
    updateFileThumbnail(fileId, thumbnail) {
      // Update in main files array
      const fileIndex = this.files.findIndex((f) => f.id === fileId);
      if (fileIndex !== -1) {
        this.files[fileIndex].thumbnail = thumbnail;
      }
    },

    // Update loading state for a specific file
    updateFileLoadingState(fileId, isLoading) {
      const fileIndex = this.files.findIndex((f) => f.id === fileId);
      if (fileIndex !== -1) {
        this.files[fileIndex].thumbnailLoading = isLoading;
      }
    },

    // Clear thumbnail cache (for debugging)
    clearThumbnailCache() {
      thumbnailCache.clearCache();
      // Also clear thumbnails from files
      this.files.forEach((file) => {
        if (file.thumbnail) {
          delete file.thumbnail;
        }
      });
    },

    // Get cache statistics (for debugging)
    getCacheStats() {
      return thumbnailCache.getCacheStats();
    },

    async fetchTransfers() {
      // Dedupe concurrent callers (multiple TorrentList mounts, DownloadsView
      // poller + visibility resync) onto one in-flight request.
      if (transfersInflight) return transfersInflight;
      transfersInflight = (async () => {
        try {
          this.transfers = await putio.getTransfers();
          await this.getInfo();
        } catch (error) {
          console.error("Fetch transfers error:", error);
        }
      })().finally(() => {
        transfersInflight = null;
      });
      return transfersInflight;
    },

    async addTransfer(url, parentId = 0) {
      try {
        const transfer = await putio.addTransfer(url, parentId);
        await this.fetchTransfers();
        return transfer;
      } catch (error) {
        console.error("Add transfer error:", error);
        throw error;
      }
    },

    async deleteFile(fileId, options = {}) {
      try {
        await putio.deleteFile(fileId);

        // Immediately update local state to remove the deleted file
        this.files = this.files.filter((f) => f.id !== fileId);

        // Cancel any transfer whose file_id points at the now-gone file
        // BEFORE refreshing the transfers list. put.io keeps the transfer
        // row alive after you delete its downloaded file, with status =
        // COMPLETED + the stale file_id. Without this cleanup, TorrentList
        // would keep rendering a "Play" button on Discover rows pointing
        // at a 404 in the library.
        const orphaned = (this.transfers || []).filter(
          (t) => t && t.file_id === fileId,
        );
        if (orphaned.length > 0) {
          // Optimistic local removal so the row flips back to "idle"
          // (Download button) instantly, before the API roundtrip.
          const orphanedIds = new Set(orphaned.map((t) => t.id));
          this.transfers = this.transfers.filter((t) => !orphanedIds.has(t.id));
          // Fire-and-forget the put.io cancels in parallel; the next
          // fetchTransfers below resyncs from the authoritative state
          // even if one fails.
          await Promise.allSettled(
            orphaned.map((t) => putio.cancelTransfer(t.id)),
          );
          await this.fetchTransfers();
        }

        // Context-aware refresh based on options
        if (options.refreshContext === "root" || !options.refreshContext) {
          // Optional: still refresh root list from API if you want authoritative state
          await this.fetchFiles();
        }

        await this.getInfo();

        return true;
      } catch (error) {
        console.error("Delete file error:", error);
        throw error;
      }
    },

    async cleanCompletedTransfers() {
      try {
        await putio.cleanTransfers();
        // Refresh the transfers list after cleaning
        await this.fetchTransfers();
        return true;
      } catch (error) {
        console.error("Clean transfers error:", error);
        throw error;
      }
    },

    // Self-healing cleanup: scan transfers for any whose file_id points
    // at a put.io file that no longer exists (e.g. deleted before
    // deleteFile's orphan-cancel landed, or deleted through the put.io
    // web UI directly) and cancel them. Triggered from MediaView when
    // /media/:id 404s -- by the time the user got there via a stale
    // "Play" chip on Discover, we already know the file is gone.
    // Idempotent and safe to call repeatedly.
    async cleanOrphanedTransfersForFile(fileId) {
      const orphaned = (this.transfers || []).filter(
        (t) => t && t.file_id === fileId,
      );
      if (orphaned.length === 0) return;
      const orphanedIds = new Set(orphaned.map((t) => t.id));
      this.transfers = this.transfers.filter((t) => !orphanedIds.has(t.id));
      await Promise.allSettled(orphaned.map((t) => putio.cancelTransfer(t.id)));
      // Resync so any concurrent fetches don't re-populate the orphans.
      await this.fetchTransfers();
    },

    // Cancel a single transfer. Works for in-progress AND completed/finished
    // transfers — Put.io's Cancel removes the transfer record either way.
    // Does NOT delete the underlying downloaded file from your library;
    // only the transfer entry. Use deleteFile for that.
    async cancelTransfer(id) {
      try {
        await putio.cancelTransfer(id);
        // Optimistic local removal so the row disappears immediately.
        this.transfers = this.transfers.filter((t) => t.id !== id);
        return true;
      } catch (error) {
        // Resync from server if the cancel failed midway through.
        await this.fetchTransfers();
        console.error("Cancel transfer error:", error);
        throw error;
      }
    },

    async emptyTrash() {
      try {
        await putio.emptyTrash();
        // Refresh account info to update disk usage
        await this.getInfo();
        return true;
      } catch (error) {
        console.error("Empty trash error:", error);
        throw error;
      }
    },

    // Download operations
    async downloadFile(fileId) {
      try {
        // For single files, try to get direct download URL first
        const file = await putio.getFile(fileId);
        if (file.download_url) {
          return { type: "direct", url: file.download_url };
        }

        // Fallback to download links
        const downloadLinksResponse = await putio.createDownloadLinks([fileId]);
        const downloadLinks = await putio.getDownloadLinks(
          downloadLinksResponse.id,
        );

        return {
          type: "download_links",
          id: downloadLinksResponse.id,
          links: downloadLinks.links,
        };
      } catch (error) {
        console.error("Download file error:", error);
        throw error;
      }
    },

    async downloadFolder(folderId) {
      try {
        // For folders, we need to create a zip
        const zipResponse = await putio.createZip([folderId]);

        // The response structure might be different, let's check for common patterns
        const zipId =
          zipResponse.zip_id || zipResponse.id || zipResponse.zip?.id;

        if (!zipId) {
          console.error("No zip ID found in response:", zipResponse);
          throw new Error("Failed to get zip ID from response");
        }

        return {
          type: "zip",
          id: zipId,
          status: "creating",
        };
      } catch (error) {
        console.error("Download folder error:", error);
        throw error;
      }
    },

    async downloadMultipleFiles(fileIds) {
      try {
        // For multiple files, create download links
        const downloadLinksResponse = await putio.createDownloadLinks(fileIds);
        const downloadLinks = await putio.getDownloadLinks(
          downloadLinksResponse.id,
        );

        return {
          type: "download_links",
          id: downloadLinksResponse.id,
          links: downloadLinks.links,
        };
      } catch (error) {
        console.error("Download multiple files error:", error);
        throw error;
      }
    },

    async getDownloadStatus(type, id) {
      try {
        if (type === "zip") {
          const zipResponse = await putio.getZip(id);

          // Check if zip is ready
          if (zipResponse.zip_status === "DONE" && zipResponse.url) {
            return {
              status: "ready",
              url: zipResponse.url,
              size: zipResponse.size,
              missing_files: zipResponse.missing_files || [],
            };
          } else if (zipResponse.zip_status === "ERROR") {
            return {
              status: "error",
              error: "ZIP creation failed",
            };
          } else {
            return {
              status: "creating",
              zip_status: zipResponse.zip_status,
            };
          }
        } else if (type === "download_links") {
          return await putio.getDownloadLinks(id);
        }
      } catch (error) {
        console.error("Get download status error:", error);
        throw error;
      }
    },

    async cancelDownload(type, id) {
      try {
        if (type === "zip") {
          return await putio.cancelZip(id);
        }
        // Download links cannot be cancelled
        return false;
      } catch (error) {
        console.error("Cancel download error:", error);
        throw error;
      }
    },

    // Transcoding operations
    async convertToMp4(fileId) {
      try {
        const response = await putio.convertToMp4(fileId);
        return response;
      } catch (error) {
        console.error("Convert to MP4 error:", error);
        throw error;
      }
    },

    async getConversionStatus(fileId) {
      try {
        const response = await putio.getConversionStatus(fileId);
        return response;
      } catch (error) {
        console.error("Get conversion status error:", error);
        throw error;
      }
    },
  },
});
